import React, { useMemo, useState } from "react";
import DateRangeIcon from "@material-ui/icons/DateRange";
import SchoolIcon from "@material-ui/icons/School";
import CreateIcon from "@material-ui/icons/Create";
import DescriptionIcon from "@material-ui/icons/Description";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";

import GenericButtonGroup from "../HomePage/Components/GenericButtonGroup";
import GenericDialog from "../HomePage/Components/GenericDialog";

const Filters = ({
  selectedDateValue,
  selectedExamType,
  selectedExams,
  onDateChange,
  onExamsChange,
  onExamTypeChange,
  exams,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  examTypeOptions = [],
}) => {
  const [open, setOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({ title: "", options: [] });

  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customFromDate, setCustomFromDate] = useState(fromDate || "");
  const [customToDate, setCustomToDate] = useState(toDate || "");

  /*--- Local state for staged changes ---*/
  const [stagedDateValue, setStagedDateValue] = useState(selectedDateValue);
  const [stagedExamType, setStagedExamType] = useState(selectedExamType);
  const [stagedExams, setStagedExams] = useState(selectedExams);

  const handleDateClick = () => {
    /*--- Reset to current value ---*/
    setStagedDateValue(selectedDateValue);

    // If customRange is already selected, add an option to edit the custom range
    const options = [
      { label: "Last 1 Exams", value: "last1Exams" },
      { label: "Last 4 Exams", value: "last4Exams" },
      { label: "Last 8 Exams", value: "last8Exams" },
      { label: "Last 16 Exams", value: "last16Exams" },
      { label: "Last 20 Exams", value: "last20Exams" },
      { label: "Till Now", value: "tillNow" },
      { label: "Custom Range", value: "customRange" },
    ];

    // If custom range is selected, add an option to edit it
    if (selectedDateValue === "customRange") {
      options.push({ label: "Edit Custom Range", value: "editCustomRange" });
    }

    setDialogConfig({
      title: "Duration",
      options: options,
    });
    setOpen(true);
  };

  const handleExamTypeClick = () => {
    setStagedExamType(selectedExamType);
    const formattedOptions = [
      { label: "None", value: "" },
      ...examTypeOptions.map((type) => ({
        label: type,
        value: type,
      })),
    ];

    setDialogConfig({
      title: "Exam Type",
      options: formattedOptions,
    });

    setOpen(true);
  };

  const handleTestsClick = () => {
    setStagedExams(selectedExams);
    if (!Array.isArray(exams)) return;
    const grouped = exams.reduce((acc, exam) => {
      const type = exam.test_type || "OTHERS";
      if (!acc[type]) acc[type] = [];
      acc[type].push({
        label: exam.testDisplayName || "Unnamed Test",
        value: exam.test_name || "",
        test_graph_name: exam.test_graph_name || "",
      });
      return acc;
    }, {});

    const groupedOptions = Object.entries(grouped).map(([group, options]) => ({
      group,
      options,
    }));
    setDialogConfig({
      title: "Tests",
      options: groupedOptions,
    });

    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setStagedDateValue(selectedDateValue);
    setStagedExamType(selectedExamType);
    setStagedExams(selectedExams);
  };

  const handleConform = () => {
    if (dialogConfig.title === "Duration") {
      onDateChange(stagedDateValue);
    } else if (dialogConfig.title === "Tests") {
      onExamsChange(stagedExams);
    } else if (dialogConfig.title === "Exam Type") {
      onExamTypeChange(stagedExamType);
    }

    setOpen(false);
  };

  const handleValueChange = (value) => {
    if (dialogConfig.title === "Duration") {
      if (value === "customRange" || value === "editCustomRange") {
        setOpen(false);
        setCustomDialogOpen(true);
      } else {
        setStagedDateValue(value);
      }
    } else if (dialogConfig.title === "Tests") {
      setStagedExams(value);
    } else if (dialogConfig.title === "Exam Type") {
      setStagedExamType(value);
    }
  };

  const buttons = [
    { label: "Duration", icon: <DateRangeIcon />, onClick: handleDateClick },
    {
      label: "Exam Type",
      icon: <DescriptionIcon />,
      onClick: handleExamTypeClick,
    },
    {
      label: "Tests",
      icon: <CreateIcon />,
      onClick: handleTestsClick,
    },
  ];

  const valuesObj = {
    Date: stagedDateValue,
    Tests: stagedExams,
    "Exam Type": stagedExamType || "",
  };

  return (
    <div>
      <GenericButtonGroup buttons={buttons} />
      <GenericDialog
        open={open}
        onClose={handleClose}
        onConform={handleConform}
        title={dialogConfig.title}
        options={dialogConfig.options}
        value={
          dialogConfig.title === "Duration"
            ? stagedDateValue || "last4Exams"
            : valuesObj[dialogConfig.title]
        }
        ifExams={dialogConfig.title === "Tests"}
        onValueChange={handleValueChange}
        checked={stagedExams.length !== 0 && stagedExams}
      />
      {/* Custom Date Range Bottom Dialog */}
      <Dialog
        open={customDialogOpen}
        onClose={() => setCustomDialogOpen(false)}
        fullWidth
        PaperProps={{
          style: {
            position: "absolute",
            bottom: 0,
            margin: 0,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            width: "100%",
            backgroundColor: "#ebeff3",
          },
        }}
      >
        <DialogTitle>Select Custom Date Range</DialogTitle>
        <DialogContent
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          <TextField
            label="From Date"
            type="date"
            value={customFromDate}
            onChange={(e) => setCustomFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            size="small"
          />
          <TextField
            label="To Date"
            type="date"
            value={customToDate}
            onChange={(e) => setCustomToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              onFromDateChange(customFromDate);
              onToDateChange(customToDate);
              onDateChange("customRange");
              setCustomDialogOpen(false);
            }}
            color="primary"
            variant="contained"
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Filters;
